import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Building2, User, Check, ChevronLeft, ChevronRight, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/common";
import { KycSection } from "@/components/KycForm";
import { SECTIONS, STEPS, DOC_TYPES, summaryFromKyc } from "@/lib/kycSchema";
import { useApi, errMsg } from "@/lib/api";

const TipoPicker = ({ onPick }) => (
  <div className="grid gap-6 md:grid-cols-2">
    {[["PM", "Persona Moral", "Empresas y sociedades mercantiles", Building2], ["PF", "Persona Física", "Personas con o sin actividad empresarial", User]].map(([v, t, d, Icon], i) => (
      <button key={v} data-testid={`tipo-${v}`} onClick={() => onPick(v)}
        className={`rise rise-${i + 1} group rounded-2xl border border-slate-200 bg-white p-8 text-left transition-all hover:-translate-y-0.5 hover:border-teal-400 hover:shadow-lg`}>
        <span className="inline-flex rounded-xl bg-teal-50 p-3 text-teal-700 transition-colors group-hover:bg-teal-600 group-hover:text-white"><Icon className="h-6 w-6" /></span>
        <h3 className="mt-6 text-2xl font-semibold text-slate-900">{t}</h3>
        <p className="mt-1 text-sm text-slate-500">{d}</p>
      </button>
    ))}
  </div>
);

const Stepper = ({ steps, step, setStep }) => (
  <ol className="flex gap-2 overflow-x-auto pb-1">
    {steps.map((s, i) => (
      <li key={s.title}>
        <button data-testid={`wizard-step-${i}`} onClick={() => setStep(i)} className={`flex items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${i === step ? "border-teal-600 bg-teal-600 text-white" : i < step ? "border-teal-200 bg-teal-50 text-teal-800" : "border-slate-200 bg-white text-slate-500"}`}>
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20 text-[10px]">{i < step ? <Check className="h-3 w-3" /> : i + 1}</span>{s.title}
        </button>
      </li>
    ))}
  </ol>
);

const DocsPicker = ({ tipo, files, setFiles }) => {
  const [docType, setDocType] = useState(DOC_TYPES[tipo][0]);
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="wizard-docs">
      <h3 className="mb-1 border-l-4 border-teal-500 pl-3 text-lg font-semibold text-slate-900">Documentos del expediente</h3>
      <p className="mb-5 pl-4 text-xs text-slate-500">PDF o imagen, máximo 15 MB por archivo</p>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Select value={docType} onValueChange={setDocType}>
          <SelectTrigger className="sm:w-80" data-testid="wizard-doc-type"><SelectValue /></SelectTrigger>
          <SelectContent>{DOC_TYPES[tipo].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-teal-400 px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-50">
          <Upload className="h-4 w-4" /> Seleccionar archivo
          <input type="file" className="hidden" accept="application/pdf,image/*" data-testid="wizard-doc-file"
            onChange={(e) => { const file = e.target.files[0]; if (file) setFiles([...files, { docType, file }]); e.target.value = ""; }} />
        </label>
      </div>
      <ul className="mt-4 divide-y divide-slate-100">
        {files.map((x, i) => (
          <li key={i} className="flex items-center justify-between py-2 text-sm">
            <span><span className="font-medium text-slate-900">{x.docType}</span> · <span className="text-slate-500">{x.file.name}</span></span>
            <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><X className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default function ClientNew() {
  const { id } = useParams();
  const api = useApi();
  const nav = useNavigate();
  const [tipo, setTipo] = useState(null);
  const [kyc, setKyc] = useState({});
  const [step, setStep] = useState(0);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) api.get(`/clients/${id}`).then((r) => { setTipo(r.data.tipo); setKyc(r.data.kyc || {}); });
  }, [api, id]);

  if (!tipo) return (<><PageHeader title="Alta de cliente" subtitle="Selecciona el tipo de persona para iniciar el expediente KYC" /><TipoPicker onPick={setTipo} /></>);

  const steps = STEPS[tipo];
  const cur = steps[step];
  const sum = summaryFromKyc(tipo, kyc);

  const submit = async () => {
    if (sum.nombre.trim().length < 2 || sum.rfc.trim().length < 10) {
      toast.error(tipo === "PM" ? "Captura Razón Social y RFC válidos" : "Captura nombre, apellido y RFC válidos");
      return setStep(0);
    }
    if (!kyc.declaratorias?.acepta) return toast.error("Debes aceptar las declaratorias");
    setSaving(true);
    try {
      const res = id ? await api.patch(`/clients/${id}`, { ...sum, kyc }) : await api.post("/clients", { tipo, ...sum, kyc });
      const cid = res.data.id;
      for (const x of files) {
        const fd = new FormData();
        fd.append("doc_type", x.docType);
        fd.append("file", x.file);
        await api.post(`/clients/${cid}/documents`, fd);
      }
      toast.success(id ? "Expediente actualizado" : "Cliente dado de alta");
      nav(`/clientes/${cid}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader title={id ? "Editar expediente" : `Alta ${tipo === "PM" ? "Persona Moral" : "Persona Física"}`} subtitle={sum.nombre || "Formulario de alta KYC"}>
        {!id && <Button variant="ghost" onClick={() => { setTipo(null); setStep(0); setKyc({}); }} data-testid="wizard-change-tipo">Cambiar tipo</Button>}
      </PageHeader>
      <Stepper steps={steps} step={step} setStep={setStep} />
      <div className="space-y-6" key={step}>
        {cur.docs && <DocsPicker tipo={tipo} files={files} setFiles={setFiles} />}
        {cur.sections.map((k) => (
          <div className="rise" key={k}><KycSection section={SECTIONS[tipo][k]} value={kyc[k]} onChange={(v) => setKyc({ ...kyc, [k]: v })} /></div>
        ))}
      </div>
      <div className="sticky bottom-0 -mx-4 flex justify-between border-t border-slate-200 bg-white/90 px-4 py-4 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-prev"><ChevronLeft className="mr-1 h-4 w-4" />Anterior</Button>
        {step < steps.length - 1 ? (
          <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => setStep(step + 1)} data-testid="wizard-next">Siguiente<ChevronRight className="ml-1 h-4 w-4" /></Button>
        ) : (
          <Button className="bg-teal-600 hover:bg-teal-700" disabled={saving} onClick={submit} data-testid="wizard-submit">{saving ? "Guardando…" : id ? "Guardar cambios" : "Dar de alta"}</Button>
        )}
      </div>
    </>
  );
}
